// Twetch identity state for /alkanes — its own slice, deliberately apart
// from the trading store: the trading store is re-derived from the chain on
// every visit, while this is a SESSION — it persists, it survives refreshes,
// and clearing it is an explicit sign-out.
//
// What persists: uid, handle, external wallet address, device PUBKEY. The
// device PRIVATE key lives in its own localStorage entry (twetch-crypto),
// outside React state — components never hold the secret, and a state dump
// can't leak it. Sign-out clears both.

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { TwetchIdentity } from './twetch-auth'
import { clearDeviceKey, loadDeviceKey, type DeviceKey } from './twetch-crypto'

interface TwetchState {
	/// The signed-in Twetch identity, or null (guest reads only).
	identity: TwetchIdentity | null
	setIdentity: (identity: TwetchIdentity) => void
	/// Forget the session: identity AND device key. The account itself lives
	/// on Twetch, rooted in the wallet — signing back in mints a fresh key.
	signOut: () => void
}

export const useTwetch = create<TwetchState>()(
	persist(
		(set) => ({
			identity: null,
			setIdentity: (identity) => set({ identity }),
			signOut: () => {
				clearDeviceKey()
				set({ identity: null })
			}
		}),
		{
			name: 'alkanes-twetch-identity',
			storage: createJSONStorage(() => localStorage),
			version: 1,
			partialize: (s) => ({ identity: s.identity })
		}
	)
)

/// The device key that matches the stored identity — null when either half
/// is missing or they disagree (a half-cleared session must read as signed
/// out, not as a session that fails every write).
export function sessionDeviceKey(identity: TwetchIdentity | null): DeviceKey | null {
	if (!identity) {
		return null
	}
	const key = loadDeviceKey()
	if (!key || key.pub !== identity.devicePubkey) {
		return null
	}
	return key
}

const SESSION_KEY_MARKED = 'alkanes-twetch-session-key-marked'

/// Sessions opened before Twetch knew about session-only keys are still
/// payable: likes would send sats to this browser's throwaway key. Tell Twetch
/// once per device key (device-key signed, no wallet popup, idempotent).
export async function markSessionDeviceKey(identity: TwetchIdentity | null): Promise<void> {
	const deviceKey = sessionDeviceKey(identity)
	if (!identity || !deviceKey) {
		return
	}
	try {
		if (window.localStorage.getItem(SESSION_KEY_MARKED) === deviceKey.pub) {
			return
		}
		const { twetchSignedPatch } = await import('./twetch-transport')
		await twetchSignedPatch(
			`/v1/users/${identity.uid}`,
			{ sessionDeviceKey: true },
			{ uid: identity.uid, deviceKey }
		)
		window.localStorage.setItem(SESSION_KEY_MARKED, deviceKey.pub)
	} catch {
		// Retried on the next visit; nothing user-facing depends on it.
	}
}
